import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import Modal from "@material-ui/core/Modal";
import MenuItem from "@material-ui/core/MenuItem";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import LocationOnIcon from "@material-ui/icons/LocationOn";

import { InputField } from "../../../../../../components/input-field/InputField";
import { InputButton } from "../../../../../../components/input-button/InputButton";
import { LocationPicker } from "../../../../../../components/location-picker/LocationPicker";
import { BusinessOwnerService } from "../../../../../../services/BusinessOwnerService";
import { CountryService } from "../../../../../../services/CountryService";
import CityService from "../../../../../../services/CityService";

interface AddBranchProps {
  businessId: number;
  isOpened: boolean;
  closeCallback: (succeed: boolean) => void;
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
  },
  modalBody: {
    width: 470,
    padding: 10,
    border: "none",
  },
}));

export const AddBranch = (props: AddBranchProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { businessId, isOpened, closeCallback } = props;
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [error, setError] = useState(false);

  const formik = useFormik({
    initialValues: {
      country_id: "",
      city_id: "",
      name: "",
      address: "",
      latitude: "",
      longitude: "",
    },
    validationSchema: Yup.object().shape({
      country_id: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseSelectBranchCountry" })
      ),
      city_id: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseSelectBranchCity" })
      ),
      name: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterBranchName" })
      ),
      address: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterBranchAddress" })
      ),
      latitude: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterBranchLatitude" })
      ),
      longitude: Yup.string().required(
        formatMessage({ id: "sharedScreen.pleaseEnterBranchLongitude" })
      ),
    }),
    onSubmit: async (values, { setSubmitting }: FormikHelpers<any>) => {
      setError(false);
      try {
        const addBranchResult = await BusinessOwnerService.addBranch({
          businessId,
          countryId: +values.country_id,
          cityId: +values.city_id,
          name: values.name,
          address: values.address,
          latitude: values.latitude,
          longitude: values.longitude,
        });
        if (addBranchResult.isSuccess && addBranchResult.data.code === 100) {
          closeCallback(true);
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
      }
      setSubmitting(false);
    },
  });

  const loadCities = async () => {
    setCities([]);
    try {
      const cities = await CityService.supportedCities(
        formik.values.country_id
      );
      if (cities.isSuccess && cities.data.code === 100) {
        const responseData = JSON.parse(JSON.stringify(cities.data.data));
        setCities(responseData);
      }
    } catch (_) {}
  };

  useEffect(() => {
    const loadCountries = async () => {
      setCountries([]);
      try {
        const countries = await CountryService.supportedCountries();
        if (countries.isSuccess && countries.data.code === 100) {
          const responseData = JSON.parse(JSON.stringify(countries.data.data));
          setCountries(responseData);
        }
      } catch (_) {}
    };
    loadCountries();
  }, []);

  const handleCountryChange = (event) => {
    formik.values.country_id = event.target.value;
    formik.values.city_id = "";
    formik.setFieldValue("country_id", event.target.value);
    loadCities();
  };

  const selectPosition = (latitude: string, longitude: string) => {
    formik.values.latitude = latitude;
    formik.values.longitude = longitude;
    formik.setFieldValue("latitude", latitude.toString());
    formik.setFieldValue("longitude", longitude.toString());
  };

  return (
    <Modal
      className={classes.modal}
      open={isOpened}
      onClose={() => closeCallback(false)}
    >
      <Paper className={classes.modalBody}>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              xs={12}
              sm={12}
              lg={12}
            >
              <Typography
                className={classes.modalTitle}
                variant="h4"
                component="h4"
              >
                <FormattedMessage id="sharedScreen.addABranch" />
              </Typography>
              <IconButton edge="end" onClick={() => closeCallback(false)}>
                <HighlightOffIcon fontSize="large" />
              </IconButton>
            </Grid>
            {error && (
              <Grid item xs={12} sm={12} lg={12}>
                <Alert severity="error">
                  <FormattedMessage id="sharedScreen.thereWasAProblemAddingTheBranch" />
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={12} lg={12}>
              <TextField
                select
                variant="outlined"
                size="small"
                value={formik.values.country_id}
                label={formatMessage({ id: "sharedScreen.country" })}
                fullWidth
                name="country_id"
                onChange={handleCountryChange}
                error={
                  formik.touched.country_id && Boolean(formik.errors.country_id)
                }
                helperText={
                  formik.touched.country_id && formik.errors.country_id
                }
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.designation}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <TextField
                select
                variant="outlined"
                size="small"
                value={formik.values.city_id}
                label={formatMessage({ id: "sharedScreen.city" })}
                fullWidth
                name="city_id"
                onChange={formik.handleChange}
                error={formik.touched.city_id && Boolean(formik.errors.city_id)}
                helperText={formik.touched.city_id && formik.errors.city_id}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.designation}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                value={formik.values.name}
                fullWidth
                label={formatMessage({ id: "sharedScreen.branchName" })}
                name="name"
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={12}>
              <InputField
                value={formik.values.address}
                fullWidth
                label={formatMessage({ id: "sharedScreen.address" })}
                name="address"
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <LocationOnIcon />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>
            <LocationPicker
              latitude={formik.values.latitude}
              onChangeLatitude={formik.handleChange}
              errorOnLatitude={
                formik.touched.latitude && Boolean(formik.errors.latitude)
              }
              helperTextOnLatitude={
                formik.touched.latitude && formik.errors.latitude
              }
              longitude={formik.values.longitude}
              onChangeLongitude={formik.handleChange}
              errorOnLongitude={
                formik.touched.longitude && Boolean(formik.errors.longitude)
              }
              helperTextOnLongitude={
                formik.touched.longitude && formik.errors.longitude
              }
              onSelectionCallback={selectPosition}
            />
            <Grid item xs={12} sm={12} lg={12} container direction="row">
              <InputButton
                disabled={formik.isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                color="secondary"
              >
                <FormattedMessage id="sharedScreen.add" />
                {formik.isSubmitting && <CircularProgress size={30} />}
              </InputButton>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Modal>
  );
};
